import { SITE_URL } from '@/config/seo';

const text = value => typeof value === 'string' ? value.trim() : '';
export const blogText = (post, field, locale = 'en') => text(locale.startsWith('es') ? post?.[`${field}_es`] : '') || text(post?.[field]);
export const blogCategory = (post, locale) => blogText(post?.category, 'name', locale);
export const blogPage = value => Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : 1;
export const blogTopic = value => typeof value === 'string' && /^[\w-]+$/.test(value) && value !== 'all' ? value : '';
export const blogPath = post => `/blog/${encodeURIComponent(post.key || post._id)}`;
export function blogDate(value, locale) {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(date) : '';
}
export function blogUrl(value, { link = false } = {}) {
  try {
    const raw = text(value);
    if (!raw) return '';
    if (link && /^#[\w-]+$/.test(raw)) return raw;
    const url = new URL(raw, SITE_URL);
    if (url.username || url.password) return '';
    return ['https:', 'http:', ...(link ? ['mailto:'] : [])].includes(url.protocol) ? url.href : '';
  } catch { return ''; }
}
export function plainBlogText(html) {
  if (!html || typeof DOMParser === 'undefined') return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('script,style,noscript').forEach(el => el.remove());
  return doc.body.textContent.replace(/\s+/g, ' ').trim();
}
export const readingMinutes = html => {
  const words = plainBlogText(html).split(/\s+/).filter(Boolean).length;
  return words ? Math.max(1, Math.ceil(words / 220)) : 0;
};

const allowedTags = new Set('P DIV SPAN SECTION H1 H2 H3 H4 H5 H6 A UL OL LI STRONG B EM I U S DEL BLOCKQUOTE PRE CODE BR HR IMG FIGURE FIGCAPTION TABLE THEAD TBODY TFOOT TR TD TH IFRAME VIDEO AUDIO SOURCE SUB SUP'.split(' '));
const dropTags = new Set('SCRIPT STYLE NOSCRIPT FORM INPUT BUTTON SELECT TEXTAREA OBJECT EMBED SVG MATH TEMPLATE LINK META BASE'.split(' '));
function trustedEmbed(src) {
  try {
    const url = new URL(src);
    return url.protocol === 'https:' && (
      (['www.youtube.com', 'www.youtube-nocookie.com'].includes(url.hostname) && url.pathname.startsWith('/embed/')) ||
      (url.hostname === 'open.spotify.com' && url.pathname.startsWith('/embed/')) ||
      (url.hostname === 'player.vimeo.com' && url.pathname.startsWith('/video/')) ||
      (url.hostname === 'w.soundcloud.com' && url.pathname.startsWith('/player/'))
    );
  } catch { return false; }
}

// Rebuild CMS markup with reader-safe elements. Inline colors and widths are
// intentionally omitted so older articles respect the current theme and screen.
export function prepareArticle(html, title = '', embedTitle = '') {
  if (typeof DOMParser === 'undefined') return { html: '', headings: [] };
  const input = new DOMParser().parseFromString(html || '', 'text/html');
  const output = document.createElement('div');
  const headings = [];
  const anchorMap = new Map();
  const normalized = value => value.replace(/\s+/g, ' ').trim().toLocaleLowerCase();
  function append(node, parent) {
    if (node.nodeType === 3) { parent.appendChild(document.createTextNode(node.textContent)); return; }
    if (node.nodeType !== 1 || dropTags.has(node.tagName)) return;
    if (!allowedTags.has(node.tagName)) { [...node.childNodes].forEach(child => append(child, parent)); return; }
    if (!headings.length && ['H1', 'H2'].includes(node.tagName) && normalized(node.textContent) === normalized(title)) return;
    const tag = node.tagName === 'H1' ? 'H2' : node.tagName;
    const el = document.createElement(tag);
    for (const attr of ['alt', 'title']) if (node.hasAttribute(attr)) el.setAttribute(attr, node.getAttribute(attr));
    if (tag === 'A') {
      const href = blogUrl(node.getAttribute('href'), { link: true });
      if (href) { el.setAttribute('href', href); if (!href.startsWith('#') && !href.startsWith(SITE_URL + '/')) { el.setAttribute('target', '_blank'); el.setAttribute('rel', 'noopener noreferrer'); } }
    }
    if (['IMG', 'SOURCE', 'VIDEO', 'AUDIO', 'IFRAME'].includes(tag)) {
      const src = blogUrl(node.getAttribute('src'));
      if (tag === 'IFRAME' && !trustedEmbed(src)) {
        if (src) { const link = document.createElement('a'); link.href = src; link.textContent = node.getAttribute('title') || embedTitle; link.target = '_blank'; link.rel = 'noopener noreferrer'; parent.appendChild(link); }
        return;
      }
      if (['IMG', 'IFRAME', 'SOURCE'].includes(tag) && !src) return;
      if (src) el.setAttribute('src', src);
      if (tag === 'IMG') { el.setAttribute('loading', 'lazy'); el.setAttribute('decoding', 'async'); if (!el.hasAttribute('alt')) el.setAttribute('alt', ''); }
      if (tag === 'IFRAME') { el.setAttribute('loading', 'lazy'); el.setAttribute('title', node.getAttribute('title') || embedTitle); el.setAttribute('allow', 'encrypted-media; fullscreen; picture-in-picture'); el.setAttribute('allowfullscreen', ''); }
      if (['VIDEO', 'AUDIO'].includes(tag)) { el.setAttribute('controls', ''); el.setAttribute('preload', 'none'); }
    }
    if (['TD', 'TH'].includes(tag)) for (const attr of ['colspan', 'rowspan']) { const n = Number(node.getAttribute(attr)); if (Number.isInteger(n) && n > 0 && n <= 100) el.setAttribute(attr, String(n)); }
    if (tag === 'OL' && /^-?\d+$/.test(node.getAttribute('start') || '')) el.setAttribute('start', node.getAttribute('start'));
    if (['H2', 'H3'].includes(tag) && node.textContent.trim()) {
      const id = `article-section-${headings.length + 1}`;
      el.id = id; el.setAttribute('tabindex', '-1');
      if (node.id) anchorMap.set(node.id, id);
      headings.push({ id, text: node.textContent.trim(), level: tag === 'H3' ? 3 : 2 });
    }
    [...node.childNodes].forEach(child => append(child, el));
    if (tag === 'TABLE') { const wrap = document.createElement('div'); wrap.className = 'journal-table-scroll'; wrap.setAttribute('tabindex', '0'); wrap.appendChild(el); parent.appendChild(wrap); }
    else parent.appendChild(el);
  }
  [...input.body.childNodes].forEach(node => append(node, output));
  output.querySelectorAll('a[href^="#"]').forEach(a => { const id = anchorMap.get(a.getAttribute('href').slice(1)); if (id) a.setAttribute('href', `#${id}`); });
  return { html: output.innerHTML, headings };
}
export function blogItems(response) {
  const items = Array.isArray(response?.items) ? response.items : response?.posts;
  if (!Array.isArray(items) || response.total === undefined || response.total === null || !Number.isFinite(Number(response.total)) || Number(response.total) < 0) throw new Error('Invalid blog response');
  return { items: items.filter(post => post && (post.key || post._id)).map(post => response.category && (!post.category || typeof post.category !== 'object') ? { ...post, category: response.category } : post), total: Number(response.total) };
}
